import { MouseEvent as ReactMouseEvent, useCallback, useRef } from 'react'
import { eventMatchesPointerModifier } from 'src/shortcuts/pointerModifiers'
import type { RenderedListRow } from '../types'

type UseRowSelectionProps = {
  multiSelectEnabled: boolean
  /** Rows in display order — multi-select ranges follow this order. */
  rendered: RenderedListRow[]
  selectedId?: string
  selectedIds?: string[]
  selectedIdSet: Set<string>
  /** Return false when navigation/selection must be aborted (caller shows a warning). */
  onSelect?: (id: string) => boolean | void
  /** Return false when navigation/selection must be aborted (caller shows a warning). */
  onSelectionChange?: (ids: string[]) => boolean | void
}

type UseRowSelectionResult = {
  handleRowClick: (id: string, event: ReactMouseEvent<HTMLElement>) => void
  /** Single-select a row programmatically; returns false when the caller vetoed the change. */
  selectRow: (id: string) => boolean
}

/**
 * Row click selection: single select, plus list-toggle and multi-select range when multi-select
 * is on.
 */
export const useRowSelection = ({
  multiSelectEnabled,
  rendered,
  selectedId,
  selectedIds,
  selectedIdSet,
  onSelect,
  onSelectionChange,
}: UseRowSelectionProps): UseRowSelectionResult => {
  /** Anchor for multi-select ranges. */
  const lastSelectionIdRef = useRef<string | null>(selectedId ?? null)

  const selectRow = useCallback(
    (id: string): boolean => {
      let selectionOk = true
      if (onSelectionChange != null) selectionOk = onSelectionChange([id]) !== false
      else if (onSelect != null) selectionOk = onSelect(id) !== false
      if (selectionOk) lastSelectionIdRef.current = id
      return selectionOk
    },
    [onSelect, onSelectionChange],
  )

  const handleRowClick = useCallback(
    (id: string, event: ReactMouseEvent<HTMLElement>) => {
      const hasListModifier =
        eventMatchesPointerModifier(event, 'listToggle') ||
        eventMatchesPointerModifier(event, 'multiSelect')
      if (!multiSelectEnabled || !hasListModifier) {
        selectRow(id)
        return
      }

      const renderedIds = rendered.map(({ item }) => item.id)
      const currentSelectedIds = selectedIds ?? (selectedId ? [selectedId] : [])
      let nextSelectedIds: string[]

      if (eventMatchesPointerModifier(event, 'multiSelect') && lastSelectionIdRef.current) {
        const startIndex = renderedIds.indexOf(lastSelectionIdRef.current)
        const endIndex = renderedIds.indexOf(id)
        if (startIndex !== -1 && endIndex !== -1) {
          const [from, to] = startIndex < endIndex ? [startIndex, endIndex] : [endIndex, startIndex]
          nextSelectedIds = Array.from(
            new Set([...currentSelectedIds, ...renderedIds.slice(from, to + 1)]),
          )
        } else {
          nextSelectedIds = Array.from(new Set([...currentSelectedIds, id]))
        }
      } else if (selectedIdSet.has(id)) {
        nextSelectedIds = currentSelectedIds.filter((selected: string) => selected !== id)
      } else {
        nextSelectedIds = [...currentSelectedIds, id]
      }

      if (onSelectionChange?.(nextSelectedIds) === false) return

      lastSelectionIdRef.current = id
      if (nextSelectedIds.length === 1 && !onSelectionChange) {
        onSelect?.(nextSelectedIds[0])
      }
    },
    [
      multiSelectEnabled,
      onSelect,
      onSelectionChange,
      rendered,
      selectedId,
      selectedIds,
      selectedIdSet,
      selectRow,
    ],
  )

  return { handleRowClick, selectRow }
}
